"use client";

import * as React from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

// ============================================
// TYPES
// ============================================
export type NumberInputSize = "sm" | "default" | "lg";

export interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "size" | "value" | "defaultValue"> {
  /** Controlled value */
  value?: number;
  /** Uncontrolled default value */
  defaultValue?: number;
  /** Called when value changes */
  onChange?: (value: number) => void;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Decimal precision */
  precision?: number;
  /** Custom format function */
  format?: (value: number) => string;
  /** Custom parse function */
  parse?: (value: string) => number;
  /** Allow mouse wheel to change value */
  allowMouseWheel?: boolean;
  /** Size preset */
  size?: NumberInputSize;
  /** Disabled state */
  isDisabled?: boolean;
}

// ============================================
// COMPONENT
// ============================================
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      value,
      defaultValue = 0,
      onChange,
      min = -Infinity,
      max = Infinity,
      step = 1,
      precision,
      format,
      parse,
      allowMouseWheel = false,
      size = "default",
      isDisabled = false,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState<number>(defaultValue);
    const [inputText, setInputText] = React.useState<string>("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    // Sync input text with value
    React.useEffect(() => {
      const displayValue = format ? format(currentValue) : currentValue.toString();
      setInputText(displayValue);
    }, [currentValue, format]);

    const clamp = (val: number): number => {
      let clamped = Math.max(min, Math.min(max, val));
      if (precision !== undefined) {
        clamped = parseFloat(clamped.toFixed(precision));
      }
      return clamped;
    };

    const updateValue = (newValue: number) => {
      const clamped = clamp(newValue);
      if (!isControlled) {
        setInternalValue(clamped);
      }
      onChange?.(clamped);
    };

    const increment = () => {
      updateValue(currentValue + step);
    };

    const decrement = () => {
      updateValue(currentValue - step);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setInputText(text);
    };

    const handleInputBlur = () => {
      const parsed = parse ? parse(inputText) : parseFloat(inputText);
      if (!isNaN(parsed)) {
        updateValue(parsed);
      } else {
        // Reset to current value
        const displayValue = format ? format(currentValue) : currentValue.toString();
        setInputText(displayValue);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        increment();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        decrement();
      } else if (e.key === "Enter") {
        handleInputBlur();
      }
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (!allowMouseWheel || isDisabled) return;
      e.preventDefault();
      if (e.deltaY < 0) {
        increment();
      } else {
        decrement();
      }
    };

    const sizeClasses = {
      sm: "h-8 text-caption px-8",
      default: "h-9 text-body px-10",
      lg: "h-10 text-body px-12",
    };

    const buttonSizeClasses = {
      sm: "h-6 w-6",
      default: "h-7 w-7",
      lg: "h-8 w-8",
    };

    return (
      <div
        data-slot="number-input"
        className={cn(styleDrivers.numberInput.root, className)}
      >
        <div className={styleDrivers.numberInput.container}>
          <button
            type="button"
            onClick={decrement}
            disabled={isDisabled || currentValue <= min}
            className={cn(
              styleDrivers.numberInput.button,
              buttonSizeClasses[size],
              "left-1"
            )}
            aria-label="Decrease value"
          >
            <Minus className={styleDrivers.numberInput.buttonIcon} />
          </button>

          <input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            type="text"
            inputMode="decimal"
            value={inputText}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onWheel={handleWheel}
            disabled={isDisabled}
            className={cn(
              styleDrivers.numberInput.input,
              sizeClasses[size]
            )}
            aria-valuemin={min !== -Infinity ? min : undefined}
            aria-valuemax={max !== Infinity ? max : undefined}
            aria-valuenow={currentValue}
            {...props}
          />

          <button
            type="button"
            onClick={increment}
            disabled={isDisabled || currentValue >= max}
            className={cn(
              styleDrivers.numberInput.button,
              buttonSizeClasses[size],
              "right-1"
            )}
            aria-label="Increase value"
          >
            <Plus className={styleDrivers.numberInput.buttonIcon} />
          </button>
        </div>
      </div>
    );
  }
);

NumberInput.displayName = "NumberInput";

// ============================================
// EXPORTS
// ============================================
export { NumberInput };
